import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, ReactNode } from 'react';
import { QueryClient } from '@tanstack/react-query';
import { User, ROLES, ROLE_GROUPS } from '@app-types';
import { hasRole } from '@utils/permissionUtils';
import authService from '@services/authServiceFactory';
import config from '../config';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  queryClient?: QueryClient;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, queryClient }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);

      // Clear React Query cache if queryClient is provided
      if (queryClient) {
        queryClient.clear();
      }

      setUser(null);
      setError(null);

      // This will redirect to /login after clearing the auth session
      const service = await authService;
      await service.logout();
    } catch (error) {
      console.error('Logout error:', error);
      setError('Logout failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [queryClient]);

  const refreshUser = useCallback(async () => {
    setIsLoading(true);
    try {
      const service = await authService;
      const currentUser = service.getCurrentUser();
      
      if (!currentUser) {
        setUser(null);
        setIsLoading(false);
        return; 
      }
      
      try {
        const accessToken = await service.getAccessToken();
        
        const response = await fetch(`${config.apiBaseUrl}/auth/me`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          // Do not call IdP logout: Keycloak check-sso would silently re-auth
          // and loop (ERR_TOO_MANY_REDIRECTS) for users rejected by /auth/me.
          console.error('Failed to fetch user from API');
          setUser(null);
          setError('Your account is not found in the system. Please contact an administrator.');
          return;
        }
      } catch (apiError) {
        console.error('API call failed:', apiError);
        setUser(null);
        setError('Unable to verify your account. Please try again or contact support.');
        return;
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const initializeAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      // Service is already initialized via factory method, just wait for it
      await authService;
      await refreshUser();
    } catch (e) {
      console.error('Auth initialization error:', e);
      setError('Failed to initialize authentication');
    } finally {
      setIsLoading(false);
    }
  }, [refreshUser]);

  const login = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const service = await authService;
      await service.loginWithFallback();
      await refreshUser();
    } catch (error) {
      console.error('Login error:', error);

      // Handle redirect requirement
      if (error instanceof Error && error.message === 'REDIRECT_REQUIRED') {
        // User will be redirected, no need to show error
        return;
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [refreshUser]);

  // Initialize auth state on app load
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const value: AuthContextType = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      logout,
      refreshUser,
      error
    }),
    [user, isLoading, login, logout, refreshUser, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Role-based permission hooks
// Multi-role support: User can have multiple roles, permissions = union of all assigned roles
export const usePermissions = () => {
  const { user } = useAuth();

  const canEvaluateItems = useCallback(
    (assessorId: number | null) => {
      if (hasRole(user, ROLE_GROUPS.FULL_ACCESS)) return true;
      if (hasRole(user, ROLES.ASSESSOR)) return user?.id === assessorId;
      return false;
    },
    [user]
  );

  const canEditAssessment = useCallback(
    (assessorId: number | null, _contractManagerId: number | null) => {
      if (hasRole(user, ROLE_GROUPS.FULL_ACCESS)) return true;
      if (hasRole(user, ROLES.ASSESSOR)) return user?.id === assessorId;
      return false;
    },
    [user]
  );

  const canViewAssessment = useCallback(
    (assessorId: number | null, _contractManagerId: number | null) => {
      if (hasRole(user, ROLE_GROUPS.FULL_ACCESS)) return true;
      if (hasRole(user, ROLES.CONTRACT_ROLE)) return true;
      if (hasRole(user, ROLES.ASSESSOR)) return user?.id === assessorId;
      return false;
    },
    [user]
  );

  const canViewContractDetails = useCallback(
    (contractManagerId: number | null, contractOwnerId: number | null) => {
      if (hasRole(user, ROLE_GROUPS.FULL_ACCESS)) return true;
      if (hasRole(user, ROLES.CONTRACT_ROLE)) {
        return user?.id === contractOwnerId || user?.id === contractManagerId;
      }
      if (hasRole(user, ROLES.ASSESSOR)) return true;
      return false;
    },
    [user]
  );

  const canViewSupplierDetails = useCallback(() => {
    if (hasRole(user, ROLES.ASSESSOR)) return true;
    return hasRole(user, ROLE_GROUPS.CONTRACT_ROLE_ACCESS);
  }, [user]);

  const canAccessSupplierDirectly = useCallback(() => {
    return (
      !hasRole(user, ROLES.EXECUTIVE) ||
      hasRole(user, [ROLES.ADMIN, ROLES.PROGRAM_LEAD, ROLES.SCHEDULER, ROLES.CONTRACT_ROLE, ROLES.ASSESSOR])
    );
  }, [user]);

  const canUploadEvidence = useCallback(
    (_contractManagerId: number | null, assessorId: number | null) => {
      if (hasRole(user, ROLE_GROUPS.FULL_ACCESS)) return true;
      if (hasRole(user, ROLES.CONTRACT_ROLE)) return true;
      if (hasRole(user, ROLES.ASSESSOR)) return user?.id === assessorId;
      return false;
    },
    [user]
  );

  const canReviewEvidence = useCallback(
    (assessorId: number | null) => {
      if (hasRole(user, ROLE_GROUPS.FULL_ACCESS)) return true;
      if (hasRole(user, ROLES.ASSESSOR)) return user?.id === assessorId;
      return false;
    },
    [user]
  );

  const canApproveAssessment = useCallback(
    (contractManagerId: number | null) => {
      if (hasRole(user, ROLE_GROUPS.FULL_ACCESS)) return true;
      if (hasRole(user, ROLES.CONTRACT_ROLE)) return false;
      return false;
    },
    [user]
  );

  return useMemo(
    () => ({
      // Role checks - true if user has this role (among possibly multiple roles)
      // isAdmin includes Program Lead (admin-equivalent for MVP)
      isAdmin: hasRole(user, ROLE_GROUPS.FULL_ACCESS),
      // isAdminStrict is the Admin role only (no Program Lead) - use ONLY for
      // surfaces that must be restricted to Admin, e.g. the Help admin module.
      isAdminStrict: hasRole(user, ROLES.ADMIN),
      isProgramLead: hasRole(user, ROLES.PROGRAM_LEAD),
      isAssessor: hasRole(user, ROLES.ASSESSOR),
      isContractRole: hasRole(user, ROLES.CONTRACT_ROLE),
      isContractManager: hasRole(user, ROLES.CONTRACT_ROLE),
      isContractOwner: hasRole(user, ROLES.CONTRACT_ROLE),
      isViewOnly: hasRole(user, ROLES.VIEW_ONLY),
      isExecutive: hasRole(user, ROLES.EXECUTIVE),
      isScheduler: hasRole(user, ROLES.SCHEDULER),

      // Permission helpers
      canCreateAssessments: hasRole(user, ROLE_GROUPS.FULL_ACCESS),
      canReassignAssessments: hasRole(user, ROLE_GROUPS.FULL_ACCESS),
      canManageUsers: hasRole(user, ROLE_GROUPS.CONTACT_USER_MANAGERS),
      canManageContracts: hasRole(user, ROLE_GROUPS.CONTRACT_CREATORS),
      canEditContracts: hasRole(user, ROLE_GROUPS.CONTRACT_EDITORS),
      canManageSuppliers: hasRole(user, ROLE_GROUPS.FULL_ACCESS),
      canCreateSuppliers: hasRole(user, ROLE_GROUPS.SUPPLIER_CREATORS),
      canDeleteSuppliers: hasRole(user, ROLE_GROUPS.SUPPLIER_DELETERS),
      canEditSuppliers: hasRole(user, ROLE_GROUPS.SUPPLIER_MANAGERS),

      // Navigation permissions - SECURITY CRITICAL
      canAccessContracts: hasRole(user, ROLE_GROUPS.CONTRACT_VIEWERS),
      canAccessSuppliers: hasRole(user, [
        ROLES.ADMIN,
        ROLES.PROGRAM_LEAD,
        ROLES.SCHEDULER,
        ROLES.ASSESSOR,
        ROLES.CONTRACT_ROLE
      ]),
      canAccessUsers: hasRole(user, ROLE_GROUPS.CONTACT_USER_VIEWERS),
      canAccessDivisions: hasRole(user, ROLE_GROUPS.FULL_ACCESS),
      canAccessDashboard: true,
      canAccessReports:
        !hasRole(user, ROLES.VIEW_ONLY) ||
        hasRole(user, [
          ROLES.ADMIN,
          ROLES.PROGRAM_LEAD,
          ROLES.ASSESSOR,
          ROLES.CONTRACT_ROLE,
          ROLES.SCHEDULER,
          ROLES.EXECUTIVE,
        ]),
      canAccessScheduler: hasRole(user, ROLE_GROUPS.SCHEDULER_ACCESS),
      canAccessAssessments: hasRole(user, ROLE_GROUPS.ASSESSMENT_VIEWERS),
      canAccessQueries: hasRole(user, ROLE_GROUPS.QUERY_ACCESS),
      canAccessNotes: hasRole(user, ROLE_GROUPS.NOTES_ACCESS),

      // Assessment-specific permissions (memoized functions)
      canEvaluateItems,
      canEditAssessment,
      canViewAssessment,

      // Contract/Supplier detail permissions (memoized functions)
      canViewContractDetails,
      canViewSupplierDetails,
      canAccessSupplierDirectly,

      // Evidence management permissions (memoized functions)
      canUploadEvidence,
      canReviewEvidence,
      canApproveAssessment
    }),
    [
      user,
      canEvaluateItems,
      canEditAssessment,
      canViewAssessment,
      canViewContractDetails,
      canViewSupplierDetails,
      canAccessSupplierDirectly,
      canUploadEvidence,
      canReviewEvidence,
      canApproveAssessment
    ]
  );
};

// Export permissions type
export type UserPermissions = ReturnType<typeof usePermissions>;

export default AuthContext;
